import React from 'react';

interface SkeletonProps {
  /**
   * The width of the skeleton
   */
  width?: string | number;
  
  /**
   * The height of the skeleton
   */
  height?: string | number;
  
  /**
   * The CSS class name to apply
   */
  className?: string;
  
  /**
   * Whether to display a shimmer effect
   */
  animate?: boolean;
  
  /**
   * The shape of the skeleton
   */
  variant?: 'text' | 'rectangle' | 'circle' | 'rounded';
}

/**
 * A skeleton loader component for content placeholders
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  className = '',
  animate = true,
  variant = 'rectangle',
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case 'text':
        return 'rounded';
      case 'circle':
        return 'rounded-full';
      case 'rounded':
        return 'rounded-xl';
      case 'rectangle':
      default:
        return '';
    }
  };

  return (
    <div
      className={`bg-gray-200 dark:bg-gray-700 ${animate ? 'animate-skeleton' : ''} ${getVariantClass()} ${className}`}
      style={{
        width: width || '100%',
        height: height || '1rem',
      }}
      aria-hidden="true"
    />
  );
};

interface SkeletonLineProps {
  /**
   * The number of lines to display
   */
  lines?: number;
  
  /**
   * The spacing between lines
   */
  spacing?: number | string;
  
  /**
   * Whether the last line should be shorter
   */
  lastLineWidth?: string;
  
  /**
   * The CSS class name to apply to each line
   */
  lineClassName?: string;
}

/**
 * Multiple skeleton lines for text content
 */
export const SkeletonText: React.FC<SkeletonLineProps> = ({
  lines = 3,
  spacing = '0.75rem',
  lastLineWidth = '70%',
  lineClassName = '',
}) => {
  return (
    <div className="flex flex-col" style={{ gap: spacing }}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          className={lineClassName}
          width={index === lines - 1 ? lastLineWidth : '100%'}
        />
      ))}
    </div>
  );
};

interface SkeletonCardProps {
  /**
   * Whether to include a header in the card
   */
  header?: boolean;
  
  /**
   * Whether to include a footer in the card
   */
  footer?: boolean;
  
  /**
   * The CSS class name to apply to the card
   */
  className?: string;
  
  /**
   * The number of text lines to display in the body
   */
  bodyLines?: number;
}

/**
 * A skeleton card component
 */
export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  header = true,
  footer = false,
  className = '',
  bodyLines = 3,
}) => {
  return (
    <div className={`card ${className}`}>
      {header && (
        <div className="flex justify-between items-center mb-5">
          <Skeleton variant="text" width="50%" height="1.5rem" />
          <Skeleton variant="circle" width="2.5rem" height="2.5rem" />
        </div>
      )}
      
      <div className="mb-6">
        <Skeleton variant="rounded" className="w-full" height="120px" />
      </div>
      
      <SkeletonText lines={bodyLines} />
      
      {footer && (
        <div className="flex justify-between items-center mt-5">
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="20%" />
        </div>
      )}
    </div>
  );
};

interface SkeletonListProps {
  /**
   * The number of items to display
   */
  count?: number;
  
  /**
   * The layout of the items
   */
  layout?: 'grid' | 'list';
  
  /**
   * The CSS class name to apply to the list
   */
  className?: string;
}

/**
 * A list of skeleton cards
 */
export const SkeletonList: React.FC<SkeletonListProps> = ({
  count = 3,
  layout = 'grid',
  className = '',
}) => {
  return (
    <div 
      className={`
        ${layout === 'grid' ? 'grid gap-6 sm:gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-4'} 
        ${className}
      `}
    >
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={index} />
      ))}
    </div>
  );
};

export default Skeleton; 